import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { BACKEND_URL, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Truck, LogOut, Plus, Pencil, Trash2, Upload, Download, FileText, Users, Package, Mail, Images, Eye } from "lucide-react";
import { toast } from "sonner";

const EQUIPMENT = ["Dry Van", "Reefer", "Flatbed", "Step Deck", "Power Only", "Box Truck"];
const STATUSES = ["available", "urgent", "covered", "cancelled"];
const CARRIER_STATUSES = ["new", "reviewed", "approved", "packet_sent"];

const emptyLoad = {
  origin_city: "", origin_state: "", dest_city: "", dest_state: "",
  equipment: "Dry Van", weight_lbs: 0, rate_usd: 0, pickup_date: "",
  delivery_date: "", commodity: "", distance_mi: 0, notes: "", status: "available",
};

function LoadForm({ open, onOpenChange, load, onSaved }) {
  const [form, setForm] = useState(emptyLoad);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(load ? { ...emptyLoad, ...load } : emptyLoad);
  }, [open, load]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        weight_lbs: Number(form.weight_lbs) || 0,
        rate_usd: Number(form.rate_usd) || 0,
        distance_mi: form.distance_mi ? Number(form.distance_mi) : null,
      };
      if (load?.id) await api.put(`/loads/${load.id}`, payload);
      else await api.post("/loads", payload);
      toast.success(load?.id ? "Load updated" : "Load posted");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0E1526] border-slate-800 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">{load?.id ? "Edit Load" : "Post New Load"}</DialogTitle>
          <DialogDescription className="text-slate-400">Details will appear on the public load board instantly.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {[
            ["origin_city", "Origin City"], ["origin_state", "Origin State"],
            ["dest_city", "Destination City"], ["dest_state", "Destination State"],
          ].map(([k, l]) => (
            <div key={k}>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">{l}</Label>
              <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-[#131C31] border-slate-700 text-white" data-testid={`load-form-${k}`} />
            </div>
          ))}
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Equipment</Label>
            <Select value={form.equipment} onValueChange={(v) => setForm({ ...form, equipment: v })}>
              <SelectTrigger data-testid="load-form-equipment" className="mt-1 bg-[#131C31] border-slate-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131C31] border-slate-700 text-white">
                {EQUIPMENT.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="load-form-status" className="mt-1 bg-[#131C31] border-slate-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131C31] border-slate-700 text-white">
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {[
            ["weight_lbs", "Weight (lbs)", "number"], ["rate_usd", "Rate (USD)", "number"],
            ["pickup_date", "Pickup Date", "date"], ["delivery_date", "Delivery Date", "date"],
            ["distance_mi", "Distance (mi)", "number"], ["commodity", "Commodity", "text"],
          ].map(([k, l, t]) => (
            <div key={k}>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">{l}</Label>
              <Input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-[#131C31] border-slate-700 text-white" data-testid={`load-form-${k}`} />
            </div>
          ))}
          <div className="col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-[#131C31] border-slate-700 text-white" data-testid="load-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="load-form-save-btn" className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">{saving ? "Saving…" : "Save Load"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoadsTab() {
  const [loads, setLoads] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/loads");
      setLoads(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/loads/${id}`);
      toast.success("Load deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-display font-bold text-2xl text-white">Manage Load Board</h3>
          <p className="text-sm text-slate-400 mt-1">Post loads that carriers see instantly on the public board.</p>
        </div>
        <Button data-testid="admin-add-load-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> New Load
        </Button>
      </div>

      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#131C31] text-slate-400">
            <tr>
              {["Lane", "Equipment", "Rate", "Weight", "Pickup", "Status", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loads.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-10 text-slate-500">No loads posted yet.</td></tr>
            ) : loads.map((l) => (
              <tr key={l.id} data-testid={`admin-load-row-${l.id}`} className="hover:bg-[#131C31]/50">
                <td className="px-4 py-3 text-white">{l.origin_city}, {l.origin_state} → {l.dest_city}, {l.dest_state}</td>
                <td className="px-4 py-3">{l.equipment}</td>
                <td className="px-4 py-3 font-mono text-amber-400">${Number(l.rate_usd).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono">{Number(l.weight_lbs).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono">{l.pickup_date}</td>
                <td className="px-4 py-3"><Badge className="border border-slate-700 bg-[#131C31] text-slate-200 font-mono uppercase text-[10px]">{l.status}</Badge></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" data-testid={`admin-edit-load-${l.id}`} onClick={() => { setEditing(l); setOpen(true); }} className="text-slate-300 hover:bg-slate-800 hover:text-white">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid={`admin-delete-load-${l.id}`} className="text-red-400 hover:bg-red-500/10 hover:text-red-300">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0E1526] border-slate-800 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this load?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">This removes it from the public board immediately.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(l.id)} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LoadForm open={open} onOpenChange={setOpen} load={editing} onSaved={load} />
    </div>
  );
}

function PacketTab() {
  const [info, setInfo] = useState({ available: false });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/packet/info");
      setInfo(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("fgb_token");
      const resp = await fetch(`${BACKEND_URL}/api/packet/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const data = await resp.json();
      setInfo(data);
      toast.success("Carrier packet uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async () => {
    try {
      await api.delete("/packet");
      toast.success("Packet removed");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <h3 className="font-display font-bold text-2xl text-white">Carrier Packet PDF Manager</h3>
      <p className="text-sm text-slate-400 mt-1 mb-6">Upload the official carrier packet PDF. It becomes available instantly on the public site.</p>

      <div className="rounded-xl border border-slate-800 bg-[#131C31] p-6 max-w-2xl">
        {info.available ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <FileText className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <div className="font-medium text-white">{info.filename}</div>
                <div className="text-xs text-slate-500 font-mono">
                  {(info.size / 1024).toFixed(1)} KB · uploaded {new Date(info.uploaded_at).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <a href={`${BACKEND_URL}/api/packet/download`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" data-testid="admin-preview-packet-btn" className="border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800 hover:text-white">
                  <Download className="h-4 w-4 mr-1.5" /> Preview current
                </Button>
              </a>
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="admin-replace-packet-btn"
                className="bg-amber-500 text-black hover:bg-amber-400 font-semibold"
              >
                <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Uploading…" : "Replace PDF"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="admin-delete-packet-btn" className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent">
                    <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0E1526] border-slate-800 text-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove the carrier packet?</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-400">Carriers won&apos;t be able to download until a new one is uploaded.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600 text-white">Remove</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Package className="h-10 w-10 text-slate-700 mx-auto mb-3" />
            <div className="font-display font-semibold text-slate-200">No packet uploaded yet</div>
            <p className="text-sm text-slate-500 mt-1 mb-5">Upload a PDF that carriers will download from the public portal.</p>
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="admin-upload-packet-btn"
              className="bg-amber-500 text-black hover:bg-amber-400 font-semibold"
            >
              <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload Carrier Packet"}
            </Button>
          </div>
        )}
        <input ref={fileRef} type="file" accept="application/pdf" onChange={onUpload} className="hidden" data-testid="admin-packet-file-input" />
      </div>
    </div>
  );
}

function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/photos");
      setPhotos(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/photos/${id}`);
      toast.success("Photo removed");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-display font-bold text-2xl text-white">Fleet Photo Gallery</h3>
          <p className="text-sm text-slate-400 mt-1">Photos appear on the public portal in the fleet gallery section.</p>
        </div>
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="admin-upload-photo-btn"
          className="bg-amber-500 text-black hover:bg-amber-400 font-semibold"
        >
          <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload Photo"}
        </Button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="admin-photo-file-input" />

      {photos.length === 0 ? (
        <div data-testid="photos-empty-state" className="rounded-xl border border-dashed border-slate-800 bg-[#0E1526]/50 p-14 text-center">
          <Images className="h-10 w-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display font-semibold text-lg text-slate-200">No fleet photos yet</div>
          <p className="text-sm text-slate-500 mt-1">Upload a photo to feature it on the public portal.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-photos-grid">
          {photos.map((p) => (
            <div key={p.id} data-testid={`admin-photo-${p.id}`} className="group relative rounded-xl overflow-hidden border border-slate-800 aspect-[4/3]">
              <img src={`${BACKEND_URL}${p.url}`} alt={p.original_filename || "fleet"} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid={`admin-photo-delete-${p.id}`} className="text-red-400 hover:bg-red-500/20 hover:text-red-300">
                      <Trash2 className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0E1526] border-slate-800 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove this photo?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">It will no longer appear on the public portal.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del(p.id)} className="bg-red-500 hover:bg-red-600 text-white">Remove</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CarrierDocsDialog({ carrier, open, onOpenChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !carrier) return;
    setLoading(true);
    api.get(`/carriers/${carrier.id}/docs`)
      .then((r) => setDocs(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, carrier]);

  const downloadUrl = (doc) => {
    const token = localStorage.getItem("fgb_token");
    return `${BACKEND_URL}/api/carrier-docs/${doc.id}/download?auth=${encodeURIComponent(token || "")}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0E1526] border-slate-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Documents — {carrier?.company_name}</DialogTitle>
          <DialogDescription className="text-slate-400">W-9, COI and authority documents attached by the carrier.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-slate-500">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="py-8 text-center text-slate-500" data-testid="carrier-docs-empty">No documents attached.</div>
        ) : (
          <div className="space-y-2" data-testid="carrier-docs-list">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#131C31] px-4 py-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileText className="h-4 w-4 text-amber-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{d.original_filename}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{d.content_type} · {(d.size / 1024).toFixed(0)} KB</div>
                  </div>
                </div>
                <a href={downloadUrl(d)} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="ghost" data-testid={`carrier-doc-download-${d.id}`} className="text-amber-400 hover:bg-amber-500/10">
                    <Download className="h-4 w-4" />
                  </Button>
                </a>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CarriersTab() {
  const [carriers, setCarriers] = useState([]);
  const [docsCarrier, setDocsCarrier] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const refresh = async () => {
    try {
      const r = await api.get("/carriers");
      setCarriers(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/carriers/${id}/status`, { status });
      toast.success("Status updated");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/carriers/${id}`);
      toast.success("Carrier removed");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-display font-bold text-2xl text-white">Carrier Applications</h3>
          <p className="text-sm text-slate-400 mt-1">{carriers.length} total submission{carriers.length === 1 ? "" : "s"}.</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#131C31] text-slate-400">
            <tr>
              {["Company", "MC/DOT", "Contact", "Fleet", "Status", "Submitted", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {carriers.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-10 text-slate-500">No carrier submissions yet.</td></tr>
            ) : carriers.map((c) => (
              <tr key={c.id} data-testid={`admin-carrier-row-${c.id}`} className="hover:bg-[#131C31]/50">
                <td className="px-4 py-3 text-white">
                  <div className="font-medium">{c.company_name}</div>
                  <div className="text-xs text-slate-500">{c.equipment_types}</div>
                </td>
                <td className="px-4 py-3 font-mono">{c.mc_dot_number}</td>
                <td className="px-4 py-3">
                  <div>{c.contact_name}</div>
                  <div className="text-xs text-slate-500">{c.phone} · {c.email}</div>
                </td>
                <td className="px-4 py-3 font-mono">{c.number_of_trucks || "—"}</td>
                <td className="px-4 py-3">
                  <Select value={c.status} onValueChange={(v) => updateStatus(c.id, v)}>
                    <SelectTrigger data-testid={`admin-carrier-status-${c.id}`} className="w-36 bg-[#131C31] border-slate-700 text-white h-8"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#131C31] border-slate-700 text-white">
                      {CARRIER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 font-mono">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" data-testid={`admin-carrier-docs-${c.id}`} onClick={() => { setDocsCarrier(c); setDocsOpen(true); }} className="text-slate-300 hover:bg-slate-800 hover:text-white">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <a href={`mailto:${c.email}?subject=${encodeURIComponent("FGB Dispatching — Carrier Packet & Setup")}&body=${encodeURIComponent(`Hi ${c.contact_name},\n\nThank you for applying with FGB Dispatching. Please find the carrier packet attached and return signed docs to support@fgbdispatching.com.\n`)}`}>
                      <Button size="sm" variant="ghost" data-testid={`admin-carrier-email-${c.id}`} className="text-amber-400 hover:bg-amber-500/10">
                        <Mail className="h-4 w-4" />
                      </Button>
                    </a>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid={`admin-carrier-delete-${c.id}`} className="text-red-400 hover:bg-red-500/10 hover:text-red-300">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0E1526] border-slate-800 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this carrier submission?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(c.id)} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CarrierDocsDialog carrier={docsCarrier} open={docsOpen} onOpenChange={setDocsOpen} />
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = () => {
    logout();
    toast.success("Signed out");
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#080C14] text-slate-200">
      <header className="border-b border-slate-800 bg-[#0E1526]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Truck className="h-4 w-4 text-amber-400" />
            </div>
            <div>
              <div className="font-display font-extrabold text-white">FGB Dispatch Console</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Admin · {user?.email}</div>
            </div>
          </div>
          <Button variant="outline" onClick={doLogout} data-testid="admin-logout-btn" className="border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent">
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-10">
        <Tabs defaultValue="loads">
          <TabsList className="bg-[#131C31] border border-slate-800">
            <TabsTrigger value="loads" data-testid="admin-tab-loads" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <FileText className="h-4 w-4 mr-1.5" /> Loads
            </TabsTrigger>
            <TabsTrigger value="packet" data-testid="admin-tab-packet" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Package className="h-4 w-4 mr-1.5" /> Packet
            </TabsTrigger>
            <TabsTrigger value="carriers" data-testid="admin-tab-submissions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Users className="h-4 w-4 mr-1.5" /> Carriers
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="admin-tab-photos" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Images className="h-4 w-4 mr-1.5" /> Photos
            </TabsTrigger>
          </TabsList>
          <TabsContent value="loads" className="mt-8"><LoadsTab /></TabsContent>
          <TabsContent value="packet" className="mt-8"><PacketTab /></TabsContent>
          <TabsContent value="carriers" className="mt-8"><CarriersTab /></TabsContent>
          <TabsContent value="photos" className="mt-8"><PhotosTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
